import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Plus, Clock, CheckCircle, XCircle, AlertCircle, Calendar,
  DoorOpen, User, Filter
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = '';
const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const STATUS_CONFIG = {
  pending: { label: 'În așteptare', icon: Clock, color: 'bg-yellow-100 text-yellow-700', border: 'border-yellow-300' },
  approved: { label: 'Aprobată', icon: CheckCircle, color: 'bg-green-100 text-green-700', border: 'border-green-300' },
  rejected: { label: 'Respinsă', icon: XCircle, color: 'bg-red-100 text-red-700', border: 'border-red-300' }
};

const EXIT_TYPES = {
  personal: 'Personal',
  medical: 'Medical',
  administrative: 'Administrativ',
  client_meeting: 'Întâlnire client',
  other: 'Altele'
};

export const OfficeExit = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [filter, setFilter] = useState('all');
  const [formData, setFormData] = useState({
    exit_date: new Date().toISOString().split('T')[0],
    exit_time: '',
    return_time: '',
    exit_type: 'personal',
    reason: ''
  });

  const isManager = user?.role === 'admin' || user?.role === 'project_manager';

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/office-exit-requests');
      setRequests(res.data);
    } catch (err) {
      toast.error('Eroare la încărcarea cererilor');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.exit_time || !formData.return_time) {
      toast.error('Completează orele de plecare și întoarcere');
      return;
    }

    try {
      await api.post('/office-exit-requests', formData);
      toast.success('Cerere trimisă cu succes!');
      setShowDialog(false);
      setFormData({
        exit_date: new Date().toISOString().split('T')[0],
        exit_time: '',
        return_time: '',
        exit_type: 'personal',
        reason: ''
      });
      loadRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la trimiterea cererii');
    }
  };

  const handleApprove = async (requestId) => {
    try {
      await api.post(`/office-exit-requests/${requestId}/approve`);
      toast.success('Cerere aprobată!');
      loadRequests();
    } catch (err) {
      toast.error('Eroare la aprobarea cererii');
    }
  };

  const handleReject = async (requestId) => {
    const reason = prompt('Motivul respingerii (opțional):');
    try {
      await api.post(`/office-exit-requests/${requestId}/reject`, { reason });
      toast.success('Cerere respinsă');
      loadRequests();
    } catch (err) {
      toast.error('Eroare la respingerea cererii');
    }
  };

  const handleDelete = async (requestId) => {
    if (!confirm('Sigur vrei să ștergi această cerere?')) return;
    try {
      await api.delete(`/office-exit-requests/${requestId}`);
      toast.success('Cerere ștearsă');
      loadRequests();
    } catch (err) {
      toast.error('Eroare la ștergerea cererii');
    }
  };

  const filteredRequests = requests.filter(r => {
    if (filter === 'all') return true;
    if (filter === 'mine') return r.user_id === user?.id;
    if (filter === 'pending') return r.status === 'pending';
    return r.status === filter;
  });

  // Parse date string to local date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('ro-RO', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-outfit text-4xl font-bold tracking-tight flex items-center gap-3">
              <DoorOpen className="w-10 h-10 text-orange-500" />
              Cereri învoiere
            </h1>
            <p className="mt-2 text-muted-foreground">
              Solicită aprobarea pentru ieșirea de la birou
            </p>
          </div>
          <Button onClick={() => setShowDialog(true)} data-testid="new-exit-request-btn">
            <Plus className="w-4 h-4 mr-2" />
            Cerere Nouă
          </Button>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <Button 
            variant={filter === 'all' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('all')}
          >
            Toate
          </Button>
          <Button 
            variant={filter === 'mine' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('mine')}
          >
            Cererile mele
          </Button>
          <Button 
            variant={filter === 'pending' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('pending')}
          >
            În așteptare
          </Button>
          <Button 
            variant={filter === 'approved' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('approved')}
          >
            Aprobate
          </Button>
        </div>

        {/* Requests List */}
        <div className="grid gap-4">
          {filteredRequests.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                <DoorOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nu există cereri de învoiere</p>
              </CardContent>
            </Card>
          ) : (
            filteredRequests.map(request => {
              const statusConfig = STATUS_CONFIG[request.status];
              const StatusIcon = statusConfig.icon;
              const isMine = request.user_id === user?.id;
              
              return (
                <Card key={request.id} className={`border-l-4 ${statusConfig.border}`}>
                  <CardContent className="py-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Badge className={statusConfig.color}>
                            <StatusIcon className="w-3 h-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                          <Badge variant="outline">
                            {EXIT_TYPES[request.exit_type] || request.exit_type}
                          </Badge>
                          {isMine && (
                            <Badge variant="secondary">Cererea mea</Badge>
                          )}
                        </div>
                        
                        <div className="flex items-center gap-4 text-sm mb-2">
                          <span className="flex items-center gap-1">
                            <User className="w-4 h-4 text-slate-400" />
                            <strong>{request.user_name}</strong>
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-slate-400" />
                            {formatDate(request.exit_date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-slate-400" />
                            {request.exit_time} - {request.return_time}
                          </span>
                        </div>
                        
                        {request.reason && (
                          <p className="text-sm text-muted-foreground mt-2">
                            <strong>Motiv:</strong> {request.reason}
                          </p>
                        )}
                        
                        {request.rejection_reason && (
                          <p className="text-sm text-red-600 mt-2">
                            <strong>Motiv respingere:</strong> {request.rejection_reason}
                          </p>
                        )}
                        
                        {request.approved_by_name && (
                          <p className="text-xs text-muted-foreground mt-2">
                            {request.status === 'approved' ? 'Aprobată' : 'Respinsă'} de {request.approved_by_name}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        {/* Manager approval buttons */}
                        {isManager && request.status === 'pending' && !isMine && (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-green-600 hover:bg-green-50"
                              onClick={() => handleApprove(request.id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Aprobă
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleReject(request.id)}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Respinge
                            </Button>
                          </>
                        )}
                        
                        {/* Delete button for own pending requests */}
                        {isMine && request.status === 'pending' && (
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-600"
                            onClick={() => handleDelete(request.id)}
                          >
                            Șterge
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* New Request Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-outfit text-2xl flex items-center gap-2">
                <DoorOpen className="w-6 h-6 text-orange-500" />
                Cerere Nouă de Învoiere
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Data</Label>
                  <Input 
                    type="date"
                    value={formData.exit_date}
                    onChange={(e) => setFormData({...formData, exit_date: e.target.value})}
                    required
                    data-testid="exit-date-input"
                  />
                </div>
                <div>
                  <Label>Tip învoiere</Label>
                  <Select 
                    value={formData.exit_type} 
                    onValueChange={(v) => setFormData({...formData, exit_type: v})}
                  >
                    <SelectTrigger data-testid="exit-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(EXIT_TYPES).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Ora plecare</Label>
                  <Input 
                    type="time"
                    value={formData.exit_time}
                    onChange={(e) => setFormData({...formData, exit_time: e.target.value})}
                    required
                    data-testid="exit-time-input"
                  />
                </div>
                <div>
                  <Label>Ora întoarcere</Label>
                  <Input 
                    type="time"
                    value={formData.return_time}
                    onChange={(e) => setFormData({...formData, return_time: e.target.value})}
                    required
                    data-testid="return-time-input"
                  />
                </div>
              </div>
              
              <div>
                <Label>Motiv (opțional)</Label>
                <Textarea
                  value={formData.reason}
                  onChange={(e) => setFormData({...formData, reason: e.target.value})}
                  placeholder="Descrie motivul învoierii..."
                  rows={3}
                  data-testid="exit-reason-input"
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                  Anulează
                </Button>
                <Button type="submit" data-testid="submit-exit-request-btn">
                  Trimite Cererea
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default OfficeExit;
